import { Button } from '@/components/ui/button';
import { pendingDeletionQuery } from '@/features/privacy/pending-deletion-query';
import { RecoveryCountdown } from '@/features/privacy/recovery-countdown';
import { useRestoreWorkspace } from '@/features/privacy/use-workspace-deletion';
import { useSession } from '@/lib/session';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/restore-organization')({
  component: OrganizationRecovery,
});
function OrganizationRecovery() {
  const session = useSession();
  const pending = useQuery({
    ...pendingDeletionQuery(session.data?.user?.id ?? ''),
    enabled: !!session.data?.user,
    retry: false,
    refetchInterval: 60_000,
  });
  const restore = useRestoreWorkspace();
  return (
    <main className="mx-auto max-w-lg p-6 py-16 space-y-5">
      <h1 className="text-2xl font-semibold">Organization recovery</h1>
      {session.isLoading ? (
        <p>Checking your account…</p>
      ) : session.isError || session.data?.unavailable ? (
        <p>
          Your account could not be checked. Refresh this page to try again.
        </p>
      ) : !session.data?.user ? (
        <>
          <p>
            Sign in as the clinic owner to check or restore a pending deletion.
          </p>
          <Button asChild>
            <a href="/sign-in?redirect=%2Frestore-organization">Sign in</a>
          </Button>
        </>
      ) : pending.isLoading ? (
        <p>Checking the recovery window…</p>
      ) : pending.isError ? (
        <>
          <p>
            {pending.error.message || 'Recovery details could not be loaded.'}
          </p>
          <Button variant="outline" onClick={() => pending.refetch()}>
            Try again
          </Button>
        </>
      ) : pending.data?.pending ? (
        <>
          <p>
            <strong>{pending.data.name}</strong> is pending deletion.
          </p>
          {pending.data.canRestore ? (
            <RecoveryCountdown recoverUntil={pending.data.recoverUntil}>
              <Button
                disabled={restore.isPending}
                onClick={() => restore.mutate()}
              >
                {restore.isPending ? 'Restoring…' : 'Restore organization'}
              </Button>
            </RecoveryCountdown>
          ) : session.data.user.orgRole !== 'owner' ? (
            <p>Only the clinic owner can restore it.</p>
          ) : (
            <p>
              The recovery window has ended and permanent cleanup may have
              begun.
            </p>
          )}
        </>
      ) : (
        <>
          <p>Your organization is not pending deletion.</p>
          <Button asChild>
            <a href="/dashboard">Open workspace</a>
          </Button>
        </>
      )}
    </main>
  );
}
